// @flow
import React, {useRef, useState} from 'react';

import {CodeBlock} from 'docs/components';
import Box from 'sail/Box';
import {UnstyledLink} from 'sail/UnstyledLink';
import {Caption, Text} from 'sail/Text';
import Group from 'sail/Group';
import Icon from 'sail/Icon';
import HoverHandler from 'sail/handler/HoverHandler';
import {breakpoints, ContainerSizeListener} from 'sail/contrib/Responsive';

import * as Sentry from '@sentry/react';

import {THEMES} from './constants';
import type {State, Dispatch} from './state';

type Props = {|
  state: State,
  dispatch: Dispatch,
|};

type InfoBoxProps = {|
  title: string,
  body: string,
|};

type ThemeProps = {|
  themeKey: string,
  isActive: boolean,
  handleClick: Function,
|};

type ThemeControlProps = {|
  state: State,
  dispatch: Dispatch,
  isMobile: boolean,
|};

const MAX_LINES = 3;

const InfoBox = ({title, body}: InfoBoxProps) => {
  return (
    <Box style={{width: '160px'}}>
      <Group direction="vertical" spacing={4}>
        <Text size={14} lineHeight={20} color="white" weight="medium">
          {title}
        </Text>
        <Caption size={12} lineHeight={16} color="disabled">
          {body}
        </Caption>
      </Group>
    </Box>
  );
};

const Theme = ({themeKey, isActive, handleClick}: ThemeProps) => {
  const labelColor = (isSelected, isHovering) => {
    if (isSelected) {
      return 'blue400';
    }
    if (isHovering) {
      return 'white';
    }
    return 'disabled';
  };

  return (
    <HoverHandler>
      {(hoverProps, isHovering) => (
        <UnstyledLink {...hoverProps} onClick={handleClick}>
          <Group direction="vertical" alignItems="center">
            <img
              src={THEMES[themeKey].icon}
              alt={`${THEMES[themeKey].label} icon`}
              style={{
                border: isActive ? '2px solid #6c8eef' : '2px solid #212D63',
                borderRadius: '5px',
                cursor: isActive ? 'default' : 'pointer',
                opacity: isActive || isHovering ? '100%' : '95%',
                width: '64px',
              }}
            />
            <Caption
              color={labelColor(isActive, isHovering)}
              size={12}
              lineHeight={16}
              weight="regular"
              style={{cursor: 'default'}}
              wrap={false}
            >
              {THEMES[themeKey].label}
            </Caption>
          </Group>
        </UnstyledLink>
      )}
    </HoverHandler>
  );
};

const ArrowButton = ({handleClick, direction}) => {
  const horizontalPosition =
    direction === 'left' ? {left: '184px'} : {right: '24px'};
  return (
    <UnstyledLink
      onClick={handleClick}
      style={{
        background: '#A4CDFE',
        borderRadius: '50px',
        width: '48px',
        height: '48px',
        position: 'absolute',
        top: '12px',
        zIndex: '100',
        ...horizontalPosition,
      }}
    >
      <Box margin={{left: 16, vertical: 8}}>
        {direction === 'left' ? (
          <Icon icon="previous" hoverable />
        ) : (
          <Icon icon="next" hoverable />
        )}
      </Box>
    </UnstyledLink>
  );
};

const ThemeControl = ({state, dispatch, isMobile}: ThemeControlProps) => {
  const ref = useRef();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const handleScroll = () => {
    if (
      ref.current &&
      ref.current.scrollWidth - ref.current.clientWidth ===
        ref.current.scrollLeft
    ) {
      setShowRightArrow(false);
    } else {
      setShowRightArrow(true);
    }
    if (ref.current && ref.current.scrollLeft !== 0) {
      setShowLeftArrow(true);
    } else {
      setShowLeftArrow(false);
    }
  };

  const handleArrowClick = (direction) => {
    if (ref.current) {
      if (direction === 'right') {
        ref.current.scrollLeft += 80;
      } else {
        ref.current.scrollLeft -= 80;
      }
      handleScroll();
    }
  };

  const handleThemeChange = (key) => {
    if (key !== state.theme) {
      dispatch({kind: 'THEME_CHANGED', theme: key});
    }
  };

  const itemData: Array<any> = Object.values(THEMES);
  if (itemData[0] && itemData[0].sortingWeight) {
    itemData.sort((a, b) => a.sortingWeight - b.sortingWeight);
  }

  const bps = [
    {name: 'notOverflow', breakpoint: breakpoints.widthAtOrAbove(580)},
  ];

  return (
    <Box
      padding={{right: 24, left: 20, bottom: 12, top: isMobile ? 12 : 0}}
      divider={{color: 'dark', side: 'bottom'}}
      style={{
        background: '#212D63',
        position: 'relative',
        bottom: isMobile ? '0px' : '-16px',
      }}
      flex={{direction: 'row'}}
    >
      {isMobile ? null : (
        <InfoBox
          title="Customize it"
          body="Create a custom theme to match your brand or start with one of ours:"
        />
      )}
      <Box
        style={{
          maxWidth: '625px',
          overflow: 'scroll',
          scrollBehavior: 'smooth',
        }}
        padding={isMobile ? {bottom: 12} : {left: 20}}
        boxRef={ref}
        className="SourceCodeBlock-themeControl"
        onScroll={handleScroll}
      >
        <ContainerSizeListener breakpoints={bps}>
          {(breakpoint) => (
            <>
              {!showLeftArrow ||
              isMobile ||
              breakpoint === 'notOverflow' ? null : (
                <ArrowButton
                  handleClick={() => handleArrowClick('left')}
                  direction="left"
                />
              )}
              <Group direction="horizontal" spacing={24}>
                {itemData.map(({key}) => (
                  <Theme
                    key={key}
                    themeKey={key}
                    isActive={key === state.theme}
                    handleClick={(e) => handleThemeChange(key)}
                  />
                ))}
              </Group>
              {!showRightArrow ||
              isMobile ||
              breakpoint === 'notOverflow' ? null : (
                <ArrowButton
                  handleClick={() => handleArrowClick('right')}
                  direction="right"
                />
              )}
            </>
          )}
        </ContainerSizeListener>
      </Box>
    </Box>
  );
};

const Code = ({state}: {|state: State|}) => {
  let finalCode = "An error occurred. We're looking into it!";

  const rawStr = JSON.stringify(THEMES[state.theme].appearance, null, 2);

  /**
   * This nested regex convert double quotation to single quotations for Appearance
   * API specific keys and values, remove double quotations for all the other keys,
   * add proper quotations within values, and remove unnecessary new line escape.
   * Example:
   * raw:           ".Tab": {
   *                  "border": "none",
   *                  "fontFamily": "\'Segoe UI\', \n     Oxygen"
   *                }
   *
   * formatted:     '.Tab': {
   *                  border: 'none',
   *                  fontFamily: '"Segoe UI", Oxygen'
   *                }
   */

  // This try-catch is set in place to prevent unsupport RegExp in cross-browser (#ir-tawny-solar)
  // TODO (graceg): Remove the try-catch block when the RegExp is verfied to be peaceful in cross-browser
  let appearanceKeyValueRegExp = null;
  let objKeyRegExp = null;

  try {
    appearanceKeyValueRegExp = new RegExp(
      '"\\.[^\\n\\r]+":|"\\*":|:[\\s]*"[^:\\n\\r]+"',
      'gm',
    );

    objKeyRegExp = new RegExp('"[^:\\n\\r]+":', 'gm');
  } catch (e) {
    if (Sentry && Sentry.captureMessage) {
      Sentry.captureMessage(
        `Failed to generate regular expression rule for Payment Element Appearance Demo: ${e.message}`,
      );
    }
  }

  if (appearanceKeyValueRegExp && objKeyRegExp) {
    const formattedStr = rawStr
      .replace(appearanceKeyValueRegExp, (match) => match.replace(/"/g, "'"))
      .replace(objKeyRegExp, (match) => match.replace(/"/g, ''))
      .replace(/\\'/g, '"')
      .replace(/\\n\s*/g, ' ');
    finalCode = `
const appearance = ${formattedStr};

// Pass the appearance object to the Elements instance
const elements = stripe.elements({clientSecret, appearance});`;
  }

  const isHidden = rawStr.split('\n').length > MAX_LINES;

  return isHidden ? (
    <CodeBlock
      language="javascript"
      filename="Checkout.js"
      maxLines={MAX_LINES}
    >
      {finalCode}
    </CodeBlock>
  ) : (
    <CodeBlock language="javascript" filename="Checkout.js">
      {finalCode}
    </CodeBlock>
  );
};

const SourceCodeBlock = ({state, dispatch}: Props) => {
  const bps = [{name: 'mobile', breakpoint: breakpoints.widthAtOrBelow(490)}];
  return (
    <ContainerSizeListener breakpoints={bps}>
      {(breakpoint) => (
        <Box
          flex={{direction: 'column'}}
          style={{
            background: '#212D63',
            marginBottom: '-16px',
          }}
        >
          <ThemeControl
            state={state}
            dispatch={dispatch}
            isMobile={breakpoint === 'mobile'}
          />
          {breakpoint === 'mobile' ? null : (
            <Code state={state} key={state.theme} />
          )}
        </Box>
      )}
    </ContainerSizeListener>
  );
};

export default SourceCodeBlock;
